/* eslint-disable no-param-reassign */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { EMPTY_FILTER_QUERY } from "./consts";

import type { FilterQueryType } from "./types";

const name = "bulkPayments";

type BulkPaymentsState = {
  byId: Record<string, API.BulkPayment>;
  list: string[]; // all IDs in display order
  pageMap: Record<string, string[]>; // page number: array of IDs
  next: string | null;
  selected: API.BulkPayment | null;
  total: number;
  filters: FilterQueryType;
};

const initialState: BulkPaymentsState = {
  byId: {},
  list: [],
  pageMap: {},
  selected: null,
  next: null,
  total: 0,
  filters: EMPTY_FILTER_QUERY,
};

const slice = createSlice({
  name,
  initialState,
  reducers: {
    upsertPage: (
      state,
      action: PayloadAction<{
        pageNumber: number;
        results: API.BulkPayment[];
        total: number;
      }>,
    ) => {
      const { results, total, pageNumber } = action.payload;

      results.forEach((bulkPayment: API.BulkPayment) => {
        state.byId[bulkPayment.id] = bulkPayment;
      });

      state.pageMap[pageNumber] = results.map(
        (bulkPayment: API.BulkPayment) => bulkPayment.id,
      );
      state.total = total;
    },

    appendResults: (
      state,
      action: PayloadAction<{
        results: API.BulkPayment[];
        total: number;
      }>,
    ) => {
      const { results, total } = action.payload;

      const existingIds = new Set(state.list);
      results.forEach((bulkPayment: API.BulkPayment) => {
        state.byId[bulkPayment.id] = bulkPayment;
        if (!existingIds.has(bulkPayment.id)) {
          state.list.push(bulkPayment.id);
          existingIds.add(bulkPayment.id);
        }
      });
      state.total = total;
    },

    replaceResults: (
      state,
      action: PayloadAction<{
        results: API.BulkPayment[];
        total: number;
      }>,
    ) => {
      const { results, total } = action.payload;
      results.forEach((bulkPayment: API.BulkPayment) => {
        state.byId[bulkPayment.id] = bulkPayment;
      });
      state.list = results.map((bulkPayment) => bulkPayment.id);
      state.total = total;
    },

    updateSelected: (state, action: PayloadAction<API.BulkPayment | null>) => {
      const { payload } = action;
      if (payload) {
        state.selected = payload;
      } else {
        state.selected = null;
      }
    },

    updateFilters: (
      state,
      action: PayloadAction<Partial<BulkPaymentsState["filters"]>>,
    ) => {
      const { payload } = action;
      state.filters = {
        ...state.filters,
        ...payload,
      };
    },

    resetState: (state) => {
      Object.assign(state, initialState);
    },
  },
});

export const { actions } = slice;

export default {
  [name]: slice.reducer,
};
